import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { ROLE_LABELS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "inventory_manager" };

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setItems((await api.get("/users")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/users/${editing}`, form);
      else await api.post("/users", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete user?")) return; try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const toggleAccounts = async (u) => {
    const has = (u.permissions || []).includes("accounts");
    const perms = has ? (u.permissions || []).filter((p) => p !== "accounts") : [...(u.permissions || []), "accounts"];
    try { await api.put(`/users/${u.id}/permissions`, { permissions: perms }); toast.success(has ? "Accounts access removed" : "Accounts access granted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users" subtitle="Team members & role permissions"
        action={<Button className="rounded-none gap-2" data-testid="create-user-btn" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}><Plus className="w-4 h-4" /> New User</Button>} />

      {items.length === 0 ? <EmptyState title="No users" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Accounts access</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} className="hover:bg-secondary/60" data-testid="user-row">
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="font-mono text-sm">{u.email}</TableCell>
                  <TableCell><span className="text-xs font-mono uppercase bg-secondary px-2 py-0.5">{ROLE_LABELS[u.role]}</span></TableCell>
                  <TableCell>
                    {u.role === "admin" ? <span className="text-xs text-muted-foreground">Full (admin)</span>
                      : <Switch checked={(u.permissions || []).includes("accounts")} onCheckedChange={() => toggleAccounts(u)} data-testid={`accounts-access-${u.id}`} />}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" onClick={() => { setForm({ ...u, password: "" }); setEditing(u.id); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                      {u.id !== user.id && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(u.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} User</DialogTitle><DialogDescription>Create or edit a team member and their role.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" className="rounded-none mt-1" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="user-email-input" /></div>
            <div><Label>Password {editing && <span className="text-muted-foreground">(leave blank to keep)</span>}</Label><Input type="password" className="rounded-none mt-1" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required={!editing} data-testid="user-password-input" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="inventory_manager">Inventory Manager</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-user-btn">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
